import React, { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { StanCard, StanToggle } from '@/components';
import type { UserPreference } from '@/lib/api/settings/types';
import { useUser, useUserPreferencesMutation } from '@/queries';
import { stanNotify } from '@/lib/stanNotify';

interface Preference {
    identifiers: string[];
    name: string;
    helperText: string;
    status: boolean;
    channel: string;
    type: 'platform-to-creator' | 'creator-to-fan';
}

// Bad design, this data should be initialized in the backend, change if time allows
const PREFERENCE_DATA: Preference[] = [
    {
        identifiers: ['stan-platform-creator-purchase-meeting'],
        name: 'Calendar Bookings',
        helperText: 'Receive an email when someone creates a new calendar booking',
        status: true,
        channel: 'email',
        type: 'platform-to-creator',
    },
    {
        identifiers: ['stan-platform-creator-purchase-fulfillment'],
        name: 'Orders That Require Fulfillment',
        helperText:
            'Receive an email when someone purchases a Personalized Video Response product prompting you to fulfill order',
        status: true,
        channel: 'email',
        type: 'platform-to-creator',
    },
    {
        identifiers: [
            'stan-platform-creator-purchase-digital-download',
            'stan-platform-creator-purchase-membership',
            'stan-platform-creator-purchase-course',
            'stan-platform-creator-purchase-community',
        ],
        name: 'Purchase Confirmations',
        helperText: 'Receive an email notification each time a customer purchases something from you',
        status: true,
        channel: 'email',
        type: 'platform-to-creator',
    },
    {
        identifiers: ['stan-platform-creator-capture-lead'],
        name: 'Lead Captured',
        helperText: 'Receive an email notification each time you capture a new lead',
        status: true,
        channel: 'email',
        type: 'platform-to-creator',
    },
    {
        identifiers: [
            'stan-platform-creator-membership-cancellation-scheduled',
            'stan-platform-creator-membership-cancelled',
        ],
        name: 'Membership Cancellations',
        helperText: 'Receive an email each time a customer cancels a subscription or when their access expires',
        status: true,
        channel: 'email',
        type: 'platform-to-creator',
    },
    {
        identifiers: [
            'stan-platform-fan-subscription-payment-confirmation',
            'stan-community-fan-subscription-reminder-email',
        ],
        name: 'Recurring Payments',
        helperText: 'Send an email to subscribers each time they make a recurring payment',
        status: true,
        channel: 'email',
        type: 'creator-to-fan',
    },
];

/**
 * Merges default preference data with user's saved preferences
 */
function loadPreferencesFromUser(userPreferences: UserPreference[] | undefined): Preference[] {
    if (!userPreferences) {
        return PREFERENCE_DATA;
    }

    const updatedPreferences = [...PREFERENCE_DATA];
    userPreferences.forEach(prefData => {
        const preferenceIndex = updatedPreferences.findIndex(
            p => p.identifiers.includes(prefData.identifier) && p.channel === prefData.channel
        );
        if (preferenceIndex !== -1 && updatedPreferences[preferenceIndex]) {
            updatedPreferences[preferenceIndex].status = prefData.status;
        }
    });

    return updatedPreferences;
}

export const CommunicationTab: React.FC = () => {
    const { user } = useUser();

    // Initialize preferences with user data
    const [preferenceData, setPreferenceData] = useState<Preference[]>(() =>
        loadPreferencesFromUser(user?.data.communication_preferences)
    );
    const [saveStates, setSaveStates] = useState<Record<string, 'idle' | 'saving' | 'saved' | 'error'>>({});

    const { mutate: updatePreferencesMutation } = useUserPreferencesMutation();

    const handlePreferencesUpdate = useEffectEvent((userPrefs: UserPreference[] | undefined) => {
        const updatedPreferences = loadPreferencesFromUser(userPrefs);
        setPreferenceData(updatedPreferences);
    });

    // Update preferences when user data changes
    useEffect(() => {
        handlePreferencesUpdate(user?.data.communication_preferences);
    }, [user?.data.communication_preferences]);

    const updatePreferences = (fieldName: string, updatedPreferences: Preference[]) => {
        setSaveStates(prev => ({ ...prev, [fieldName]: 'saving' }));

        // Translate Preference objects to UserPreference objects
        const userPreferences: UserPreference[] = updatedPreferences.flatMap(preference =>
            preference.identifiers.map(identifier => ({
                identifier,
                name: preference.name,
                status: preference.status,
                channel: preference.channel,
            }))
        );

        updatePreferencesMutation(userPreferences, {
            onSuccess: () => {
                setSaveStates(prev => ({ ...prev, [fieldName]: 'saved' }));

                // Reset save state after 2 seconds
                setTimeout(() => {
                    setSaveStates(prev => ({ ...prev, [fieldName]: 'idle' }));
                }, 2000);

                stanNotify({
                    type: 'success',
                    title: 'Success!',
                    text: 'Preferences updated successfully.',
                });
            },
            onError: () => {
                setSaveStates(prev => ({ ...prev, [fieldName]: 'error' }));

                stanNotify({
                    type: 'error',
                    title: 'Error',
                    text: 'Unable to update preferences. Please try again later.',
                });
            },
        });
    };

    const handlePreferenceChange = (preferenceName: string, enabled: boolean) => {
        const updatedPreferences = preferenceData.map(p => (p.name === preferenceName ? { ...p, status: enabled } : p));
        setPreferenceData(updatedPreferences);
        updatePreferences(preferenceName, updatedPreferences);
    };

    const platformToCreatorPreferences = useMemo(
        () => preferenceData.filter(p => p.type === 'platform-to-creator'),
        [preferenceData]
    );

    const creatorToFanPreferences = useMemo(
        () => preferenceData.filter(p => p.type === 'creator-to-fan'),
        [preferenceData]
    );

    return (
        <StanCard>
            <div className='max-w-3xl'>
                {/* Platform to Creator Notifications */}
                <div className='mb-8'>
                    <h4 className='mb-4'>To Me</h4>
                    <div className='space-y-4'>
                        {platformToCreatorPreferences.map((preference, index) => (
                            <div key={`c-${index}`} className='py-3'>
                                <div className='flex items-start gap-3'>
                                    <StanToggle
                                        toggleSize='sm'
                                        checked={preference.status}
                                        onChange={e => handlePreferenceChange(preference.name, e.target.checked)}
                                        className='mt-0.5'
                                    />
                                    <div className='flex-1'>
                                        <div className='mb-1 flex items-center gap-2'>
                                            <h5>{preference.name}</h5>
                                            {saveStates[preference.name] === 'saving' && (
                                                <div className='size-4 animate-spin rounded-full border-b-2' />
                                            )}
                                            {saveStates[preference.name] === 'saved' && (
                                                <div className='text-gray text-caption-1-regular'>Saved!</div>
                                            )}
                                        </div>
                                        <p className='text-gray text-caption-1-regular'>{preference.helperText}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Creator to Fan Notifications */}
                <div>
                    <h4 className='mb-4'>To Customers</h4>
                    <div className='space-y-4'>
                        {creatorToFanPreferences.map((preference, index) => (
                            <div key={`f-${index}`} className='py-3'>
                                <div className='flex items-start gap-3'>
                                    <StanToggle
                                        toggleSize='sm'
                                        checked={preference.status}
                                        onChange={e => handlePreferenceChange(preference.name, e.target.checked)}
                                        className='mt-0.5'
                                    />
                                    <div className='flex-1'>
                                        <div className='mb-1 flex items-center gap-2'>
                                            <div className='text-heading-h5'>{preference.name}</div>
                                            {saveStates[preference.name] === 'saving' && (
                                                <div className='size-4 animate-spin rounded-full border-b-2' />
                                            )}
                                            {saveStates[preference.name] === 'saved' && (
                                                <div className='text-gray text-caption-1-regular'>Saved!</div>
                                            )}
                                        </div>
                                        <p className='text-gray text-caption-1-regular'>{preference.helperText}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </StanCard>
    );
};

export default CommunicationTab;
